// Gemeinsamer Rahmen des UI-Kits: Link-Baustein der Anwendung, Ebenen (z-index) und die schwebenden Dienste
// (Tooltips, Toasts, confirm()). Die Hülle setzt <UiProvider> einmal um die Anwendung.
import { Tooltip as BaseTooltip } from '@base-ui/react/tooltip'
import { createContext, use, type AnchorHTMLAttributes, type ComponentType, type ReactNode, type Ref } from 'react'

export type LinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & { href: string; ref?: Ref<HTMLAnchorElement> }

const PlainLink = ({ href, ...rest }: LinkProps) => <a href={href} {...rest} />

const LinkContext = createContext<ComponentType<LinkProps>>(PlainLink)

/** Der Link-Baustein der Anwendung (die Hülle reicht einen react-router-Adapter herein). */
export const useLink = (): ComponentType<LinkProps> => use(LinkContext)

// ---- Ebenen ---------------------------------------------------------------------------------------------
// Jede schwebende Ebene meldet ihren z-index als CSS-Ausdruck. Was darin geöffnet wird (Menü im Dialog,
// Tooltip im Toast), liegt automatisch eine Stufe darüber.

const LayerContext = createContext<string | null>(null)

export function useLayer(base: string): string {
  const parent = use(LayerContext)
  return parent ? `calc(${parent} + 1)` : base
}

export function LayerProvider({ z, children }: { z: string; children: ReactNode }) {
  return <LayerContext value={z}>{children}</LayerContext>
}

export const Z = {
  sticky: 'var(--z-sticky)',
  shell: 'var(--z-shell)',
  popover: 'var(--z-popover)',
  drawer: 'var(--z-drawer)',
  modal: 'var(--z-modal)',
  toast: 'var(--z-toast)',
  palette: 'var(--z-palette)',
  scanner: 'var(--z-scanner)',
} as const

// ---- Anbieter -------------------------------------------------------------------------------------------

export interface UiProviderProps {
  /** Link-Baustein der Anwendung; Standard ist ein gewöhnliches <a> */
  link?: ComponentType<LinkProps>
  children: ReactNode
}

export function UiProviderBase({ link, children }: UiProviderProps) {
  const content = (
    // Tooltip nach 400 ms; benachbarte Tooltips folgen ohne Wartezeit (design-system §4.2)
    <BaseTooltip.Provider delay={400} closeDelay={0} timeout={300}>
      {children}
    </BaseTooltip.Provider>
  )
  return link ? <LinkContext value={link}>{content}</LinkContext> : content
}
